import { validateDocumentInputs, type CeremonyInputDescription, type DocumentInputs } from '@witness/shared/document-inputs';
import { DocumentInputFields } from './document-input-fields';
import { useCallback, useEffect, useState } from 'react';
import { CeremonyLocationPicker } from './ceremony-location-picker';
import type { TrustedLocation } from '../../lib/trusted-locations';
import { ApiNotConfiguredError, getCeremonyInputDescription, type CreateCeremonyInput } from '../../lib/witness-api';

const TEMPLATE_OPTIONS = [{ id: 'trial-v0', label: 'trial-v0' }] as const;

type FormState =
  | { status: 'idle' }
  | { status: 'submitting' }
  | { status: 'error'; message: string };

export function CeremonyCreationForm({
  tenantId,
  submitCeremony,
  onCreated,
  initialSubjectName = '',
  subjectHint,
}: {
  tenantId: string;
  submitCeremony: (input: CreateCeremonyInput) => Promise<void>;
  onCreated: () => void;
  initialSubjectName?: string;
  subjectHint?: string;
}) {
  const [templateId, setTemplateId] = useState<string>(TEMPLATE_OPTIONS[0].id);
  // An explicit edit (including clearing the field) wins over late Clio data.
  const [subjectNameOverride, setSubjectName] = useState<string | null>(null);
  const subjectName = subjectNameOverride ?? initialSubjectName;
  const [deliveryMode, setDeliveryMode] = useState<'location' | 'manual'>('location');
  const [selectedLocation, setSelectedLocation] = useState<TrustedLocation | null>(null);
  const [manualEmail, setDeliveryEmail] = useState('');
  const deliveryEmail = deliveryMode === 'location' ? selectedLocation?.emailToPrint ?? '' : manualEmail;
  const [form, setForm] = useState<FormState>({ status: 'idle' });
  const [documentInputs, setDocumentInputs] = useState<DocumentInputs>({});
  const [description, setDescription] = useState<{ tenantId: string; value: CeremonyInputDescription } | null>(null);
  const [descriptionError, setDescriptionError] = useState<string | null>(null);
  const [attempt, retry] = useState(0);
  const currentDescription = description?.tenantId === tenantId && description.value.templateId === templateId
    ? description.value : null;
  useEffect(() => {
    const controller = new AbortController();
    setDescription(null);
    setDescriptionError(null);
    getCeremonyInputDescription(tenantId, templateId, controller.signal).then(value => {
      if (!controller.signal.aborted) setDescription({ tenantId, value });
    }).catch(error => {
      if (!controller.signal.aborted) setDescriptionError(error instanceof Error ? error.message : 'Unable to load template inputs.');
    });
    return () => controller.abort();
  }, [tenantId, templateId, attempt]);

  const submit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      const name = subjectName.trim();
      const email = deliveryEmail.trim();
      if (!name || !email || !currentDescription) return;
      const validated = validateDocumentInputs(currentDescription.documentInputs, documentInputs);
      if (!validated.success) { setForm({ status: 'error', message: validated.message }); return; }
      setForm({ status: 'submitting' });
      try {
        await submitCeremony({ templateId, subjectName: name, deliveryEmail: email,
          ...(Object.keys(validated.values).length ? { documentInputs: validated.values } : {}) });
        setDocumentInputs({});
        setSubjectName(null);
        setDeliveryEmail('');
        setSelectedLocation(null);
        setForm({ status: 'idle' });
        onCreated();
      } catch (err) {
        const message =
          err instanceof ApiNotConfiguredError
            ? err.message
            : err instanceof Error
              ? err.message
              : 'Create failed';
        setForm({ status: 'error', message });
      }
    },
    [submitCeremony, templateId, subjectName, deliveryEmail, onCreated, currentDescription, documentInputs],
  );

  return (
    <form
      onSubmit={submit}
      className="flex flex-col gap-3 rounded border border-neutral-200 bg-neutral-50 p-4"
    >
      <h2 className="font-serif text-xl">Create ceremony</h2>
      {subjectHint && <p className="text-sm text-neutral-600">{subjectHint}</p>}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1">
          <label
            htmlFor="templateId"
            className="text-xs font-medium uppercase tracking-wider text-neutral-500"
          >
            Template
          </label>
          <select
            id="templateId"
            value={templateId}
            onChange={(e) => { setTemplateId(e.target.value); setDocumentInputs({}); setForm({ status: 'idle' }); }}
            className="rounded border border-neutral-300 bg-white px-3 py-2 text-sm focus:border-neutral-900 focus:outline-none"
            disabled={form.status === 'submitting'}
          >
            {TEMPLATE_OPTIONS.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label
            htmlFor="subjectName"
            className="text-xs font-medium uppercase tracking-wider text-neutral-500"
          >
            Subject name
          </label>
          <input
            id="subjectName"
            type="text"
            value={subjectName}
            onChange={(e) => setSubjectName(e.target.value)}
            placeholder="e.g. Bob Smith"
            maxLength={200}
            className="rounded border border-neutral-300 px-3 py-2 text-sm focus:border-neutral-900 focus:outline-none"
            disabled={form.status === 'submitting'}
          />
        </div>
      </div>
      {currentDescription ? <DocumentInputFields fields={currentDescription.documentInputs} values={documentInputs}
        onChange={setDocumentInputs} disabled={form.status === 'submitting'} />
        : descriptionError ? <p role="alert" className="text-sm text-red-700">{descriptionError}{' '}
          <button type="button" className="underline" onClick={() => retry(value => value + 1)}>Retry</button>
        </p> : <p role="status" className="text-sm">Loading template inputs…</p>}
      <div className="rounded border border-neutral-200 bg-white p-4">
        {deliveryMode === 'location' ? <>
          <CeremonyLocationPicker selected={selectedLocation} onSelect={setSelectedLocation} disabled={form.status === 'submitting'} />
          <button type="button" disabled={form.status === 'submitting'} onClick={() => setDeliveryMode('manual')}
            className="mt-4 text-sm underline">Enter delivery email manually</button>
        </> : <div className="flex flex-col gap-2">
          <label htmlFor="deliveryEmail" className="text-sm font-medium">Document delivery email</label>
          <input id="deliveryEmail" type="email" value={manualEmail} onChange={event => setDeliveryEmail(event.target.value)}
            placeholder="store123@theupsstore.com" disabled={form.status === 'submitting'}
            className="rounded border border-neutral-300 px-3 py-2 text-sm" />
          <button type="button" disabled={form.status === 'submitting'} onClick={() => { setSelectedLocation(null); setDeliveryMode('location'); }}
            className="self-start text-sm underline">Find a location by ZIP instead</button>
        </div>}
      </div>
      {form.status === 'error' && (
        <p className="text-sm text-red-700">{form.message}</p>
      )}
      <div>
        <button
          type="submit"
          disabled={
            form.status === 'submitting' || !currentDescription ||
            !subjectName.trim() ||
            !deliveryEmail.trim()
          }
          className="rounded bg-neutral-900 px-4 py-2 text-sm font-medium text-neutral-50 hover:bg-neutral-700 disabled:cursor-not-allowed disabled:bg-neutral-400"
        >
          {form.status === 'submitting' ? 'Creating…' : 'Create ceremony'}
        </button>
      </div>
    </form>
  );
}

